import { generateHash } from 'crm-index-visualization-core/core/model/hash';
import { datesHash } from '../util/storageMangager';
import { isFeAdded, isInterimView } from 'crm-index-visualization-core/core/utils/vizSettingsAdaptor';
import { extractLegacyTableConfig, convertLegacyTableToBackendView } from './legacy-adapters';
const VIZ_SETTING_STORAGE_PREFIX = 'vizsetting';

/**
 * Represents visualization settings for a specific visualization type.
 * Replaces VizConfig with simplified approach - no versioning, just settings package.
 * Uses different storage keys to avoid JSON parsing conflicts with VizConfig.
 */
export class VizSetting {
  /**
   * Create a new VizSetting
   * @param vizType The name of the visualization type (e.g., 'BOARD', 'table', 'GANTT_CHART')
   * @param config The configuration/settings for the visualization type
   */
  constructor(vizType, config, id) {
    this.visualizationType = normalizeVizType(vizType);
    this.settings = config;
    this.id = id;
  }

  /**
   * Creates a new VizSetting with updated properties
   * @param updates Partial updates to apply to the config
   * @returns A new VizSetting instance with the updates applied
   */
  with(updates) {
    const updatedConfig = Object.assign({}, this.settings, updates);
    return new VizSetting(this.visualizationType, updatedConfig, this.id);
  }

  /**
   * Get the client cache key for this setting
   */
  static getClientCacheKey(hash) {
    return `${VIZ_SETTING_STORAGE_PREFIX}-${hash}`;
  }

  /**
   * Get the client cache key for this instance
   */
  async getClientCacheKey() {
    return VizSetting.getClientCacheKey(await this.hash());
  }

  /**
   * Generate a hash for this setting
   */
  hash() {
    return generateHash(this);
  }

  /**
   * Load a VizSetting from client cache
   * @throws if setting cannot be loaded
   */
  static async fromClientCache(hash, clientStorage) {
    const key = VizSetting.getClientCacheKey(hash);
    const stored = await clientStorage.getItem(key);
    if (!stored) {
      throw new Error(`No viz setting found for hash: ${hash}`);
    }
    const parsed = JSON.parse(stored);
    const {
      visualizationType,
      settings,
      id
    } = parsed;
    return new VizSetting(visualizationType, settings, id);
  }

  /**
   * Save this setting to client cache
   * @returns hash that can be used to retrieve the setting later
   */
  async toClientCache(clientStorage) {
    const serialized = {
      visualizationType: this.visualizationType,
      settings: this.settings,
      id: this.id
    };
    const hash = await this.hash();
    const key = await this.getClientCacheKey();
    await Promise.all([clientStorage.setItem(key, JSON.stringify(serialized)), datesHash(key, Date.now(), clientStorage)]);
    return hash;
  }

  /**
   * Creates a VizSetting instance from a backend view.
   * Extracts visualization settings from the adapted backend view or returns defaults.
   * @param beView - Backend view (potentially adapted with viz settings)
   * @param vizType - The visualization type to extract settings for
   * @throw Error if no viz setting found for view and viz type
   * @returns VizSetting instance with extracted or default configuration
   * @example VizSetting.fromBEView(adaptedView, 'table') // extracts table settings
   */
  static fromBEView(beView, vizType) {
    vizType = normalizeVizType(vizType);
    if (vizType === 'TABLE') {
      const legacySettings = extractLegacyTableConfig(beView);
      return new VizSetting(vizType, legacySettings, -2);
    }
    if (isInterimView(beView) && beView.currentViewVisualization === vizType) {
      const parsed = JSON.parse(beView.visualizationSettingsString);
      // FIX ME: remove custom code that adds FE-added settings
      if (isFeAdded(parsed)) {
        throw new Error(`Interim view ${beView.id} has FE-added settings for ${vizType}`);
      }
      return new VizSetting(vizType, parsed.settings, parsed.id);
    }
    throw new Error(`No viz setting found for view: ${beView.id} and viz type: ${vizType}`);
  }

  /**
   * Applies this VizSetting to a backend view with visualization settings.
   * Only updates the settings when visualization types match.
   * For table visualization, implements dual-write to both new and legacy formats.
   * @param beView - Backend view with current visualization settings to update
   * @returns Updated view with new settings if types match, original view if types don't match
   * @throws Never throws - safely handles type mismatches
   * @example vizSetting.toBEView(viewWithMatchingVizType) // Updates settings
   * @example vizSetting.toBEView(viewWithDifferentVizType) // Returns original view
   */
  toBEView(beView) {
    const parsed = JSON.parse(beView.visualizationSettingsString);
    if (beView.currentViewVisualization === this.visualizationType) {
      const updatedView = Object.assign({}, beView, {
        visualizationSettingsString: JSON.stringify(Object.assign({}, parsed, {
          settings: this.settings
        })),
        visualizationSettingsMap: {
          [this.visualizationType]: {
            id: this.id,
            visualizationType: this.visualizationType,
            settings: this.settings
          }
        }
      });

      // Dual-write for table: also write to legacy format
      if (this.visualizationType === 'TABLE') {
        const tableSettings = this.settings;

        // Use legacy adapter to convert table settings to backend view format
        const legacyView = convertLegacyTableToBackendView(updatedView, tableSettings);
        return Object.assign({}, updatedView, {
          columns: legacyView.columns,
          state: legacyView.state
        });
      }

      // TODO: Support DataWell.
      return updatedView;
    }
    // else we need to write to 'visualizationSettingsMap'
    const existingUpSertMap = 'visualizationSettingsMap' in beView ? beView.visualizationSettingsMap : {};
    const updatedView = Object.assign({}, beView, {
      visualizationSettingsMap: Object.assign({}, existingUpSertMap, {
        [this.visualizationType]: this.settings
      })
    });
    return updatedView;
  }

  /**
   * Returns default VizSetting instance for a visualization type
   * @param vizType - The visualization type ('BOARD', 'table', 'GANTT_CHART', 'REPORTING')
   * @returns Default VizSetting instance. Board returns VizSetting with boardProperty, others return empty settings
   * @example VizSetting.default('BOARD') // new VizSetting('BOARD', {boardProperty: 'hs_pipeline_stage'})
   */
  static default(vizType) {
    switch (vizType) {
      case 'BOARD':
        return new VizSetting('BOARD', {
          boardProperty: 'hs_pipeline_stage'
        }, -1);
      case 'TABLE':
        return new VizSetting('TABLE', {
          columns: [],
          numberOfFrozenColumns: 0
        }, -1);
      case 'GANTT_CHART':
        return new VizSetting('GANTT_CHART', {}, -1);
      case 'REPORTING':
        return new VizSetting('REPORTING', {}, -1);
      default:
        return new VizSetting(vizType, {}, -1);
    }
  }
}

/**
 * Maps page type to viz type.
 */
export function normalizeVizType(vizTypeOrPageType) {
  switch (vizTypeOrPageType) {
    case 'list':
      return 'TABLE';
    case 'board':
      return 'BOARD';
    case 'gantt':
      return 'GANTT_CHART';
    case 'report':
      return 'REPORTING';
    case 'map':
      return 'MAP';
    case 'datawell':
      return 'DATA_WELL';
    default:
      return vizTypeOrPageType.toUpperCase();
  }
}

/**
 * Maps viz type to page type.
 */
export function denormalizeVizType(vizTypeOrPageType) {
  switch (vizTypeOrPageType) {
    case 'TABLE':
      return 'list';
    case 'BOARD':
      return 'board';
    case 'GANTT_CHART':
      return 'gantt';
    case 'REPORTING':
      return 'report';
    case 'MAP':
      return 'map';
    case 'DATA_WELL':
      return 'datawell';
    default:
      return vizTypeOrPageType;
  }
}

/**
 * Maps visualization types to their corresponding setting types
 */